import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  Mail, RefreshCw, Send, Clock, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, MessageSquare, ArrowLeft, Bell,
  Brain, Loader2, X, Paperclip, FileText, Download, Eye
} from 'lucide-react';

const CONVERSATION_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-500', icon: Clock },
  email_sent: { label: 'Email Sent', color: 'bg-blue-500', icon: Send },
  awaiting_response: { label: 'Awaiting Response', color: 'bg-yellow-500', icon: Clock },
  responded: { label: 'Responded', color: 'bg-green-500', icon: CheckCircle },
  quoted: { label: 'Quoted', color: 'bg-emerald-500', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-500', icon: AlertCircle },
  no_response: { label: 'No Response', color: 'bg-gray-400', icon: AlertCircle }
};

const VendorResponsesDashboard = () => {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [rfqItems, setRfqItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingReplies, setCheckingReplies] = useState(false);
  const [expandedConversation, setExpandedConversation] = useState(null);
  const [emails, setEmails] = useState({});
  const [loadingEmails, setLoadingEmails] = useState({});
  const [lastChecked, setLastChecked] = useState(null);

  // Right panel tab state (Analysis / AI Chat)
  const [rightPanelTab, setRightPanelTab] = useState('analysis');

  // AI Chat state
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);

  // Reply state
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyResult, setReplyResult] = useState(null);

  // AI Draft email state
  const [draftEmail, setDraftEmail] = useState(null);

  // Email analysis state
  const [analyzingConversation, setAnalyzingConversation] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Email Modal state (like normal RFQ)
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [activeVendorTab, setActiveVendorTab] = useState(0);

  useEffect(() => {
    fetchData();
  }, [rfqId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // /rfq/{rfq_id} returns rfq, items, conversations, quotes all together
      const [rfqResponse, convResponse, quotesResponse] = await Promise.all([
        apiClient.get(`/api/rfq-workflow/rfq/${rfqId}`),
        apiClient.get(`/api/rfq-workflow/conversations/${rfqId}`),
        apiClient.get(`/api/rfq-workflow/rfq/${rfqId}/quotes`).catch(() => ({ quotes: [] }))
      ]);

      if (rfqResponse?.rfq) {
        setRfq(rfqResponse.rfq);
      }
      // Items come from rfqResponse, not separate endpoint
      if (rfqResponse?.items) {
        setRfqItems(rfqResponse.items);
      }
      if (convResponse?.conversations) {
        setConversations(convResponse.conversations);
      }
      if (quotesResponse?.quotes) {
        setQuotes(quotesResponse.quotes);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForReplies = async () => {
    try {
      setCheckingReplies(true);
      const response = await apiClient.post(`/api/rfq-workflow/check-replies/${rfqId}`);

      if (response) {
        setLastChecked(new Date());
        // Refresh conversations to show updated status
        await fetchData();

        if (response.new_emails_count > 0) {
          alert(`Found ${response.new_emails_count} new email(s) from vendors!`);
        } else {
          alert('No new replies found.');
        }
      }
    } catch (error) {
      console.error('Error checking replies:', error);
      alert('Failed to check for replies: ' + (error.message || 'Unknown error'));
    } finally {
      setCheckingReplies(false);
    }
  };

  const loadConversationEmails = async (conversationId) => {
    if (emails[conversationId]) {
      // Already loaded
      return;
    }

    try {
      setLoadingEmails(prev => ({ ...prev, [conversationId]: true }));
      const response = await apiClient.get(`/api/rfq-workflow/conversation/${conversationId}/emails`);

      if (response?.emails) {
        setEmails(prev => ({ ...prev, [conversationId]: response.emails }));
      }
    } catch (error) {
      console.error('Error loading emails:', error);
    } finally {
      setLoadingEmails(prev => ({ ...prev, [conversationId]: false }));
    }
  };

  const openAllVendorsModal = (clickedConversationId = null) => {
    // Load emails for ALL vendors (like normal RFQ)
    conversations.forEach(conv => {
      loadConversationEmails(conv.conversation_id);
    });

    // Set active tab to clicked vendor or first vendor
    if (clickedConversationId) {
      const idx = conversations.findIndex(c => c.conversation_id === clickedConversationId);
      setActiveVendorTab(idx >= 0 ? idx : 0);
    } else {
      setActiveVendorTab(0);
    }

    setRightPanelTab('analysis');
    setAiChatMessages([]);
    setDraftEmail(null);
    setReplyText('');
    setReplyResult(null);
    setShowEmailModal(true);
  };

  const toggleConversation = (conversationId) => {
    // Open modal with ALL vendors, switch to clicked vendor tab (like normal RFQ)
    openAllVendorsModal(conversationId);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setActiveVendorTab(0);
    setAiChatMessages([]);
    setDraftEmail(null);
    setReplyText('');
    setReplyResult(null);
  };

  // Load AI chat history for the current conversation
  const loadChatHistory = async (conversationId) => {
    if (!conversationId) return;

    setAiChatMessages([]); // Clear while loading

    try {
      const historyResponse = await apiClient.get(`/api/rfq-workflow/conversation/${conversationId}/chat-history`);
      if (historyResponse?.success && historyResponse?.messages) {
        setAiChatMessages(historyResponse.messages);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      setAiChatMessages([]);
    }
  };

  // Get current expanded vendor
  const getCurrentVendor = () => {
    return conversations.find(c => c.conversation_id === expandedConversation);
  };

  // Send direct reply to vendor
  const handleSendReply = async (conversationId, vendorName) => {
    if (!replyText.trim()) return;

    setSendingReply(true);
    setReplyResult(null);

    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${conversationId}/reply`, {
        reply_body: replyText.trim()
      });

      if (response.success) {
        setReplyResult({ success: true, message: `Reply sent to ${vendorName}` });
        setReplyText('');

        // Force refresh emails after short delay (clear cache first)
        setTimeout(async () => {
          setEmails(prev => ({ ...prev, [conversationId]: undefined }));
          await loadConversationEmails(conversationId);
          setReplyResult(null);
        }, 1500);
      } else {
        setReplyResult({ success: false, message: response.error || 'Failed to send reply' });
      }
    } catch (error) {
      setReplyResult({ success: false, message: error.message || 'Failed to send reply' });
    } finally {
      setSendingReply(false);
    }
  };

  // Send the drafted email
  const handleSendDraft = async () => {
    if (!draftEmail) return;

    const convId = draftEmail.conversation_id;
    const vendorName = draftEmail.vendor_name;

    setSendingReply(true);
    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${convId}/reply`, {
        reply_body: draftEmail.content
      });

      if (response.success) {
        setReplyResult({ success: true, message: `Reply sent to ${vendorName}` });
        setDraftEmail(null);

        // Force refresh emails (clear cache first)
        setTimeout(async () => {
          setEmails(prev => ({ ...prev, [convId]: undefined }));
          await loadConversationEmails(convId);
          setReplyResult(null);
        }, 1500);
      } else {
        alert('Failed to send: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to send: ' + error.message);
    } finally {
      setSendingReply(false);
    }
  };

  // Analyze vendor emails and extract quotes
  const handleAnalyzeEmails = async (conversationId) => {
    setAnalyzingConversation(conversationId);
    setAnalysisResult(null);

    try {
      const response = await apiClient.post(`/api/rfq-workflow/conversation/${conversationId}/analyze`, {
        analyze_all: true
      });

      if (response.success) {
        setAnalysisResult({
          success: true,
          message: response.message,
          quotes: response.extracted_quotes
        });

        // Refresh conversations to show updated quote status
        setTimeout(() => {
          fetchData();
          setAnalysisResult(null);
        }, 2000);
      } else {
        setAnalysisResult({
          success: false,
          message: response.error || 'Failed to analyze emails'
        });
      }
    } catch (error) {
      setAnalysisResult({
        success: false,
        message: error.message || 'Failed to analyze emails'
      });
    } finally {
      setAnalyzingConversation(null);
    }
  };

  // Handle AI Chat send
  const handleAiChat = async () => {
    const currentVendor = getCurrentVendor();
    if (!aiChatInput.trim() || !currentVendor) return;

    const userMessage = aiChatInput.trim();
    setAiChatInput('');
    setAiChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiChatLoading(true);

    try {
      // Build context with vendor info
      const context = {
        vendor_name: currentVendor.vendor_name,
        vendor_email: currentVendor.vendor_email,
        status: currentVendor.status,
        emails: emails[currentVendor.conversation_id] || []
      };

      const response = await apiClient.post(`/api/rfq-workflow/rfq/${rfqId}/chat`, {
        question: userMessage,
        context: context,
        conversation_id: currentVendor.conversation_id,
        run_id: currentVendor.workflow_run_id
      });

      if (response.success && response.answer) {
        // Check if AI used draft_response_email tool
        const hasDraft = response.draft_email != null;

        const aiMessage = {
          role: 'assistant',
          content: response.answer,
          isDraft: hasDraft
        };

        setAiChatMessages(prev => [...prev, aiMessage]);

        // If AI returned a draft email via tool calling, store for confirmation
        if (hasDraft && response.draft_email) {
          setDraftEmail({
            conversation_id: currentVendor.conversation_id,
            vendor_name: currentVendor.vendor_name,
            content: response.draft_email.body_html || response.answer,
            subject: response.draft_email.subject
          });
        }
      } else {
        setAiChatMessages(prev => [...prev, {
          role: 'assistant',
          content: response.error || 'Sorry, I could not process your request.'
        }]);
      }
    } catch (error) {
      setAiChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message || 'Failed to get AI response'}`
      }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = CONVERSATION_STATUS_CONFIG[status] || CONVERSATION_STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color} text-white`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // View PDF attachment
  const handleViewAttachment = async (conversationId, email, attachment) => {
    setPdfLoading(true);
    setPdfFilename(attachment.name || attachment.filename);
    setPdfViewerOpen(true);

    try {
      const response = await apiClient.get(
        `/api/rfq-workflow/conversation/${conversationId}/attachments/${email.id}/${attachment.id}`
      );

      if (response.success && response.attachment) {
        setPdfData(response.attachment.content_base64);
      } else {
        console.error('Failed to fetch attachment:', response.error);
        setPdfData(null);
      }
    } catch (error) {
      console.error('Error fetching attachment:', error);
      setPdfData(null);
    } finally {
      setPdfLoading(false);
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (conversationId, email, attachment) => {
    try {
      const response = await apiClient.get(
        `/api/rfq-workflow/conversation/${conversationId}/attachments/${email.id}/${attachment.id}`
      );

      if (response.success && response.attachment) {
        const link = document.createElement('a');
        link.href = `data:${response.attachment.content_type};base64,${response.attachment.content_base64}`;
        link.download = response.attachment.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getSummaryStats = () => {
    const total = conversations.length;
    const responded = conversations.filter(c =>
      ['responded', 'quoted'].includes(c.status)
    ).length;
    const awaiting = conversations.filter(c =>
      ['email_sent', 'awaiting_response'].includes(c.status)
    ).length;
    const noResponse = conversations.filter(c =>
      ['declined', 'no_response'].includes(c.status)
    ).length;

    return { total, responded, awaiting, noResponse };
  };

  const stats = getSummaryStats();

  return (
    <MainLayout title="Vendor Responses">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Vendor Responses
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {rfq ? `RFQ: ${rfq.rfq_number} - ${rfq.rfq_title}` : 'Loading...'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastChecked && (
              <span className="text-sm text-gray-500">
                Last checked: {formatDate(lastChecked)}
              </span>
            )}
            <Button
              onClick={checkForReplies}
              disabled={checkingReplies}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {checkingReplies ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Check for Replies
            </Button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Vendors</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Mail className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Responded</p>
                  <p className="text-2xl font-bold text-green-600">{stats.responded}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Awaiting Response</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.awaiting}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">No Response</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.noResponse}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RFQ Info */}
        {rfq && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Bid Deadline</p>
                  <p className="font-medium">{formatDate(rfq.end_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-medium capitalize">{rfq.status}</p>
                </div>
                <div>
                  <p className="text-gray-500">Items</p>
                  <p className="font-medium">{rfq.item_count || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{formatDate(rfq.created_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vendor Conversations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Vendor Conversations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No vendor conversations yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    className="border rounded-lg dark:border-gray-700"
                  >
                    {/* Conversation Header */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => toggleConversation(conv.conversation_id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {conv.vendor_name}
                          </p>
                          <p className="text-sm text-gray-500">{conv.vendor_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            {conv.email_count || 0} emails
                          </p>
                          <p className="text-xs text-gray-400">
                            Last: {formatDate(conv.last_email_at)}
                          </p>
                        </div>
                        {getStatusBadge(conv.status)}
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}`)}
          >
            Back to RFQ Details
          </Button>
          <div className="flex gap-3">
            <Button variant="outline">
              <Bell className="w-4 h-4 mr-2" />
              Send Reminders
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => navigate(`/rfq-workflow/rfq/${rfqId}/analyze`)}
              disabled={stats.responded === 0}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Proceed to Bid Analysis
            </Button>
          </div>
        </div>
      </div>

      {/* ========== EMAIL MODAL WITH VENDOR TABS (like normal RFQ) ========== */}
      {showEmailModal && conversations.length > 0 && (() => {
        const conv = conversations[activeVendorTab];
        if (!conv) return null;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mx-4 my-4 max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-green-600" />
                  Check All Vendor Replies
                </h3>
                <Button variant="ghost" size="sm" onClick={closeEmailModal}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Modal Body - Split View */}
              <div style={{ height: 'calc(90vh - 140px)' }}>
                <div className="flex h-full">
                  {/* ========== LEFT PANEL - Email Thread (60%) ========== */}
                  <div className="w-3/5 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full">
                    {/* Scrollable email area */}
                    <div className="flex-1 overflow-y-auto p-4">
                      {/* Summary bar */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Total <strong>{conversations.length}</strong> vendors |
                          With emails: <strong>{conversations.filter(c => emails[c.conversation_id]?.length > 0).length}</strong>
                        </p>
                      </div>

                      {/* Vendor Tabs */}
                      <div className="flex gap-1 overflow-x-auto pb-2 border-b border-gray-200 dark:border-gray-700 mb-3 sticky top-0 bg-white dark:bg-gray-800 z-10">
                        {conversations.map((vendor, idx) => (
                          <button
                            key={vendor.conversation_id}
                            onClick={() => { setActiveVendorTab(idx); setAiChatMessages([]); }}
                            className={`px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 ${
                              activeVendorTab === idx
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                            }`}
                          >
                            {vendor.vendor_name?.substring(0, 15)}{vendor.vendor_name?.length > 15 ? '...' : ''}
                            {emails[vendor.conversation_id]?.some(e => {
                              const isFromVendor = e.from?.toLowerCase().includes(vendor.vendor_email?.split(';')[0]?.toLowerCase());
                              return isFromVendor;
                            }) && (
                              <span className="w-2 h-2 rounded-full bg-green-400" />
                            )}
                          </button>
                        ))}
                      </div>

                      {/* Email Thread for selected vendor */}
                      {loadingEmails[conv.conversation_id] ? (
                        <div className="flex flex-col items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mb-3" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Loading email conversation...</p>
                        </div>
                      ) : emails[conv.conversation_id]?.length > 0 ? (
                        <div className="space-y-3">
                          {emails[conv.conversation_id].map((email, idx) => {
                            const isFromVendor = email.from?.toLowerCase().includes(conv.vendor_email?.split(';')[0]?.toLowerCase());
                            const direction = isFromVendor ? 'received' : 'sent';
                            return (
                              <div key={idx} className={`flex ${direction === 'sent' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[90%] rounded-lg shadow-sm overflow-hidden ${
                                  direction === 'sent' ? 'bg-blue-600' : 'bg-green-600'
                                }`}>
                                  {/* Header */}
                                  <div className={`px-3 py-2 text-xs text-white/90 ${direction === 'sent' ? 'bg-blue-700' : 'bg-green-700'}`}>
                                    {direction === 'sent' ? '📤 You' : `📥 ${conv.vendor_name}`} • {formatDate(email.receivedDateTime)}
                                    {email.hasAttachments && <Paperclip className="w-3 h-3 inline ml-2" />}
                                  </div>
                                  {/* Subject */}
                                  <div className="px-3 py-2 text-sm font-medium text-white">{email.subject}</div>
                                  {/* Body */}
                                  <div className="bg-white dark:bg-gray-800 p-3 text-sm text-gray-800 dark:text-gray-200">
                                    <pre className="whitespace-pre-wrap font-sans">{email.bodyPreview || email.body}</pre>

                                    {/* Attachments */}
                                    {email.attachments && email.attachments.length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                          <Paperclip className="w-3 h-3" />
                                          <span>{email.attachments.length} Attachment{email.attachments.length > 1 ? 's' : ''}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {email.attachments.map((att, attIdx) => {
                                            const isPdf = att.name?.toLowerCase().endsWith('.pdf') || att.contentType?.includes('pdf');
                                            return (
                                              <div
                                                key={attIdx}
                                                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${
                                                  isPdf
                                                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                                    : 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                                                }`}
                                              >
                                                <FileText className={`w-4 h-4 ${isPdf ? 'text-red-500' : 'text-gray-500'}`} />
                                                <span className="max-w-[150px] truncate" title={att.name}>{att.name}</span>
                                                <span className="text-gray-400">({formatFileSize(att.size)})</span>
                                                {isPdf && (
                                                  <button
                                                    onClick={() => handleViewAttachment(conv.conversation_id, email, att)}
                                                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded"
                                                    title="View PDF"
                                                  >
                                                    <Eye className="w-3.5 h-3.5 text-red-600" />
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() => handleDownloadAttachment(conv.conversation_id, email, att)}
                                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                                  title="Download"
                                                >
                                                  <Download className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                          <Mail className="w-12 h-12 mb-4 text-gray-300" />
                          <p>No emails in this conversation</p>
                        </div>
                      )}
                    </div>

                    {/* Reply Section */}
                    <div className="p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800">
                      <div className="flex gap-2">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          rows={2}
                          className="flex-1 px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 resize-none"
                          disabled={sendingReply}
                        />
                        <button
                          type="button"
                          onClick={() => handleSendReply(conv.conversation_id, conv.vendor_name)}
                          disabled={sendingReply || !replyText.trim()}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                        >
                          {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                      </div>
                      {replyResult && (
                        <p className={`mt-2 text-xs ${replyResult.success ? 'text-green-600' : 'text-red-600'}`}>
                          {replyResult.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ========== RIGHT PANEL - Analysis/AI Chat (40%) ========== */}
                  <div className="w-2/5 flex flex-col bg-gray-50 dark:bg-gray-900">
                    {/* Tab Headers */}
                    <div className="flex border-b dark:border-gray-700">
                      <button
                        onClick={() => setRightPanelTab('analysis')}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                          rightPanelTab === 'analysis'
                            ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-gray-800'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        📊 Analysis
                      </button>
                      <button
                        onClick={() => {
                          setRightPanelTab('chat');
                          loadChatHistory(conv.conversation_id);
                        }}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                          rightPanelTab === 'chat'
                            ? 'text-purple-600 border-b-2 border-purple-600 bg-white dark:bg-gray-800'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        🤖 AI Chat
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto p-4">
                      {rightPanelTab === 'analysis' ? (
                        <div className="space-y-4">
                          {/* Trigger Analysis Button */}
                          <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-purple-800 dark:text-purple-300">
                                🤖 AI Quote Extraction
                              </span>
                              <Button
                                size="sm"
                                onClick={() => handleAnalyzeEmails(conv.conversation_id)}
                                disabled={analyzingConversation === conv.conversation_id}
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                              >
                                {analyzingConversation === conv.conversation_id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Analyzing...
                                  </>
                                ) : (
                                  <>
                                    <Brain className="w-3 h-3 mr-1" />
                                    Extract Quotes
                                  </>
                                )}
                              </Button>
                            </div>
                            <p className="text-xs text-purple-600 dark:text-purple-400">
                              Analyze vendor emails to extract pricing and update quotes
                            </p>
                          </div>

                          {/* RFQ Items for this vendor */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">RFQ Items (Assigned to Vendor)</h4>
                            {(() => {
                              // Get item_ids - already parsed as array by backend
                              const itemIds = Array.isArray(conv.item_ids) ? conv.item_ids : [];
                              const vendorItems = rfqItems.filter(item => itemIds.includes(item.id));

                              if (vendorItems.length === 0) {
                                return <p className="text-xs text-gray-500">No specific items assigned</p>;
                              }

                              return vendorItems.map((item) => {
                                const quote = quotes.find(q => q.item_id === item.id && q.conversation_id === conv.conversation_id);
                                return (
                                  <div key={item.id} className="p-3 bg-white dark:bg-gray-800 rounded-lg mb-2 shadow-sm">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="font-medium text-gray-900 dark:text-white text-sm">{item.item_name}</p>
                                        <p className="text-xs text-gray-500">ID: {item.netsuite_item_id || item.item_id}</p>
                                      </div>
                                      <p className="font-semibold text-blue-600">{item.quantity} {item.unit || 'units'}</p>
                                    </div>
                                    {quote && quote.unit_price ? (
                                      <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded space-y-1">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs font-medium text-green-800 dark:text-green-300">Vendor Quote</span>
                                          <span className="text-xs text-gray-500">
                                            {quote.status === 'accepted' ? 'Accepted' : 'Quoted'}
                                          </span>
                                        </div>
                                        <p className="text-xs text-green-800 dark:text-green-300">
                                          <strong>Price:</strong> ${quote.unit_price?.toFixed(2)} / unit
                                        </p>
                                        {quote.lead_time && (
                                          <p className="text-xs text-green-700 dark:text-green-400">
                                            <strong>Lead Time:</strong> {quote.lead_time}
                                          </p>
                                        )}
                                      </div>
                                    ) : quote ? (
                                      <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                        <p className="text-xs text-blue-700 dark:text-blue-400">Response received - awaiting price</p>
                                      </div>
                                    ) : (
                                      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                                        <p className="text-xs text-yellow-700 dark:text-yellow-400">Awaiting vendor quote</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>

                          {/* Vendor Status */}
                          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <h5 className="font-medium text-blue-800 dark:text-blue-300 text-sm mb-2">Vendor Status</h5>
                            <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">{conv.vendor_name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 break-all">{conv.vendor_email}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col h-full">
                          {/* AI Chat Messages */}
                          <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                            {aiChatMessages.length === 0 ? (
                              <div className="text-center py-8 text-gray-500">
                                <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p className="text-sm font-medium">AI Email Assistant</p>
                                <p className="text-xs mt-2">Ask about this vendor:</p>
                                <ul className="text-xs mt-2 space-y-1">
                                  <li>✍️ "Draft a reply email"</li>
                                  <li>📊 "Summarize the conversation"</li>
                                  <li>❓ "What pricing did they offer?"</li>
                                </ul>
                              </div>
                            ) : (
                              aiChatMessages.map((msg, idx) => (
                                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                                    msg.role === 'user'
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-sm'
                                  }`}>
                                    <pre className="whitespace-pre-wrap font-sans text-xs">{msg.content}</pre>
                                  </div>
                                </div>
                              ))
                            )}
                            {aiChatLoading && (
                              <div className="flex justify-start">
                                <div className="bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                                  <span className="text-xs text-gray-500">AI is thinking...</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* AI Chat Input */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={aiChatInput}
                              onChange={(e) => setAiChatInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleAiChat(conv.conversation_id);
                                }
                              }}
                              placeholder="Ask about quotes..."
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700"
                              disabled={aiChatLoading}
                            />
                            <button
                              onClick={() => handleAiChat(conv.conversation_id)}
                              disabled={aiChatLoading || !aiChatInput.trim()}
                              className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="outline" onClick={closeEmailModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-500" />
                <span className="font-medium text-gray-900 dark:text-white truncate max-w-md">
                  {pdfFilename}
                </span>
              </div>
              <button
                onClick={() => {
                  setPdfViewerOpen(false);
                  setPdfData(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* PDF Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-100 dark:bg-gray-900">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : pdfData ? (
                <iframe
                  src={`data:application/pdf;base64,${pdfData}`}
                  className="w-full h-full rounded border border-gray-300 dark:border-gray-600"
                  title={pdfFilename}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <FileText className="w-16 h-16 mb-4 text-gray-300" />
                  <p>Failed to load PDF</p>
                  <p className="text-sm">Try downloading the file instead</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => {
                  setPdfViewerOpen(false);
                  setPdfData(null);
                }}
              >
                Close
              </Button>
              {pdfData && (
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = `data:application/pdf;base64,${pdfData}`;
                    link.download = pdfFilename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default VendorResponsesDashboard;
